import { useMemo } from 'react';
import { projectWithEta } from 'features/Responder/features/Tours/utils';
import { useRouteProjection } from 'features/Responder/features/Tours/features/TourMap/hooks/useRouteProjection';
import formatMinutes from 'features/Responder/features/Tours/features/TourBuilder/utils/formatMinutes';
import stopLabelFromIndex from 'features/Responder/features/Tours/features/TourBuilder/utils/stopLabelFormIndex';

/**
 * Centralize route-related derived data for TourBuilder.
 */
export function useRouteCalculations({
  startLocation,
  stops,
  routeData,
  checkpoints,
  builderTourData,
  activeTourData,
  snapToRoads,
}) {
  const isIndoor = false; // Indoor mode temporarily disabled
  const isFreePlacement = !snapToRoads;

  // Consider a stop valid if it has coordinates or a placeId.
  const hasLocation = (stop) => {
    if (!stop) return false;
    const lat = Number.parseFloat(stop.lat);
    const lng = Number.parseFloat(stop.lng);
    const hasLatLng = Number.isFinite(lat) && Number.isFinite(lng);
    const hasPlaceId = !!(stop.placeId && stop.placeId.trim());
    return hasPlaceId || hasLatLng;
  };

  const canBuild = useMemo(() => {
    if (!Array.isArray(stops) || stops.length < 1) return false;
    const hasExplicitStart = hasLocation(startLocation);
    const origin = hasExplicitStart ? startLocation : stops[0];
    const waypoints = hasExplicitStart ? stops : stops.slice(1);
    if (!origin || !hasLocation(origin)) return false;
    if (!waypoints.length) return false;

    // Destination is the last stop in list order.
    const destinationOk = hasLocation(waypoints[waypoints.length - 1]);
    return destinationOk;
  }, [startLocation, stops]);

  const canSaveRoute = useMemo(
    () => (canBuild && !!routeData) || (canBuild && isFreePlacement),
    [canBuild, routeData, isFreePlacement],
  );

  const { routePoints, routeMetrics, totalDurationSeconds } = useRouteProjection(
    routeData,
    activeTourData || builderTourData,
  );

  const projectCheckpoint = (cp) => {
    if (!routePoints.length || routeMetrics.total <= 0) return { ...cp };
    const { along, etaSeconds } = projectWithEta(routePoints, routeMetrics, totalDurationSeconds, {
      lat: cp.lat,
      lng: cp.lng,
    });
    return { ...cp, along, etaSeconds: etaSeconds ?? cp.etaSeconds };
  };

  const orderedCheckpoints = useMemo(() => {
    const enriched = checkpoints.map((cp) => projectCheckpoint(cp));
    return enriched;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkpoints, routePoints.length, routeData?.polyline, totalDurationSeconds]);

  const checkpointSignature = useMemo(() => {
    const normalized = checkpoints.map((cp) => ({
      id: cp.id,
      lat: Number.isFinite(cp.lat) ? Number(cp.lat) : null,
      lng: Number.isFinite(cp.lng) ? Number(cp.lng) : null,
      stopIndex: cp.stopIndex ?? null,
    }));
    return JSON.stringify(normalized);
  }, [checkpoints]);

  const legDurationSeconds = useMemo(() => {
    if (!Array.isArray(routeData?.legs)) return [];
    return routeData.legs.map((leg) => {
      if (typeof leg.duration === 'number') return leg.duration;
      if (typeof leg.duration === 'string') {
        const n = parseFloat(leg.duration.replace('s', ''));
        return Number.isFinite(n) ? n : 0;
      }
      return 0;
    });
  }, [routeData]);

  const waypointEtas = useMemo(() => {
    if (!legDurationSeconds.length) return [];
    const arr = [];
    let sum = 0;
    legDurationSeconds.forEach((dur) => {
      sum += dur;
      arr.push(sum);
    });
    return arr;
  }, [legDurationSeconds]);

  const checkpointBucketsForOrder = useMemo(() => new Map(), []);

  const routeWaypointOrder = useMemo(() => {
    const order = [];
    const hasExplicitStart = hasLocation(startLocation);
    const startMatchesFirstStop =
      hasExplicitStart &&
      stops.length > 0 &&
      ((startLocation.id && startLocation.id === stops[0].id) ||
        (Number(startLocation.lat) === Number(stops[0].lat) &&
          Number(startLocation.lng) === Number(stops[0].lng)));

    if (hasExplicitStart && !startMatchesFirstStop) {
      order.push({
        type: 'origin',
        id: startLocation.id || 'start',
        ref: startLocation,
        stopIdx: -1,
      });
    }

    for (let i = 0; i < stops.length; i++) {
      const legIndex = i + 1;
      const cps = checkpointBucketsForOrder.get(legIndex) || [];
      cps.forEach((cp) => order.push({ type: 'checkpoint', id: cp.id, ref: cp }));
      order.push({ type: 'stop', id: stops[i].id, ref: stops[i], stopIdx: i });
    }
    return order;
  }, [checkpointBucketsForOrder, startLocation, stops]);

  const originOffset = useMemo(
    () => (routeWaypointOrder[0]?.type === 'origin' ? 1 : 0),
    [routeWaypointOrder],
  );

  const stopEtaSeconds = useMemo(() => {
    if (!waypointEtas.length) return [];
    const derived = [];
    routeWaypointOrder.forEach((item, idx) => {
      if (item.type === 'stop') {
        const wpIdx = idx - originOffset;
        derived.push(wpIdx >= 0 ? (waypointEtas[wpIdx] ?? null) : null);
      }
    });
    return derived;
  }, [originOffset, routeWaypointOrder, waypointEtas]);

  const timelineMeta = useMemo(() => {
    const stopMeta = {};
    const checkpointMeta = {};
    if (!routeWaypointOrder.length) return { stopMeta, checkpointMeta, tripTotalSeconds: 0 };

    let cumulativeExtras = 0;
    let prevBaseEta = 0;
    let checkpointCounter = 1;
    let lastTripTotal = 0;
    let prevLabel = 'Start';

    routeWaypointOrder.forEach((item, idx) => {
      const wpIdx = idx - originOffset;
      let baseEta = Number.isFinite(waypointEtas[wpIdx])
        ? Number(waypointEtas[wpIdx])
        : prevBaseEta;
      if (
        !Number.isFinite(baseEta) &&
        item.type === 'stop' &&
        Number.isFinite(stopEtaSeconds[item.stopIdx])
      ) {
        baseEta = Number(stopEtaSeconds[item.stopIdx]);
      }
      if (!Number.isFinite(baseEta)) baseEta = prevBaseEta;

      let legSeconds = Math.max(0, baseEta - prevBaseEta);
      let timeWindowSeconds = 0;
      let stopTimeSeconds = 0;
      let currentLabel = '';

      if (item.type === 'checkpoint') {
        currentLabel = `Checkpoint ${checkpointCounter}`;
        const etaValue = Number.isFinite(item.ref?.etaSeconds)
          ? Number(item.ref.etaSeconds)
          : baseEta;
        baseEta = etaValue;
        legSeconds = etaValue;
        timeWindowSeconds = Number.isFinite(item.ref?.timeWindowSeconds)
          ? Number(item.ref.timeWindowSeconds)
          : 0;
      } else if (item.type === 'stop') {
        const fallbackStopLabel = item.stopIdx != null ? stopLabelFromIndex(item.stopIdx) : '';
        const resolvedStopLabel =
          typeof item.ref?.label === 'string' && item.ref.label.trim().length
            ? item.ref.label.trim()
            : fallbackStopLabel;
        currentLabel = resolvedStopLabel || fallbackStopLabel;
        timeWindowSeconds = Number.isFinite(Number(item.ref?.timeWindowMinutes))
          ? Number(item.ref.timeWindowMinutes) * 60
          : 0;
        stopTimeSeconds = Number.isFinite(Number(item.ref?.stopTimeMinutes))
          ? Number(item.ref.stopTimeMinutes) * 60
          : 0;
      }

      const deadlineSeconds = legSeconds + timeWindowSeconds;
      const totalSeconds = legSeconds + timeWindowSeconds + stopTimeSeconds;
      const tripTotalSeconds = baseEta + cumulativeExtras + timeWindowSeconds + stopTimeSeconds;
      const legLabel = `${prevLabel} → ${currentLabel || 'Leg'}`;

      if (item.type === 'checkpoint') {
        checkpointMeta[item.id] = {
          displayIndex: checkpointCounter++,
          legSeconds,
          deadlineSeconds,
          totalSeconds,
          tripTotalSeconds,
          legLabel,
        };
        if (currentLabel) prevLabel = currentLabel;
      } else {
        const fallbackStopLabel = item.stopIdx != null ? stopLabelFromIndex(item.stopIdx) : '';
        const label =
          typeof item.ref?.label === 'string' && item.ref.label.trim().length
            ? item.ref.label.trim()
            : fallbackStopLabel;
        stopMeta[item.id] = {
          displayName: label,
          legSeconds,
          deadlineSeconds,
          totalSeconds,
          tripTotalSeconds,
          legLabel,
        };
        if (label) {
          prevLabel = label;
        } else if (fallbackStopLabel) {
          prevLabel = fallbackStopLabel;
        }
      }

      cumulativeExtras += timeWindowSeconds + stopTimeSeconds;
      prevBaseEta = baseEta;
      lastTripTotal = tripTotalSeconds;
    });

    return { stopMeta, checkpointMeta, tripTotalSeconds: lastTripTotal };
  }, [originOffset, routeWaypointOrder, waypointEtas, stopEtaSeconds]);

  const stopMetaById = timelineMeta.stopMeta || {};
  const checkpointMetaById = timelineMeta.checkpointMeta || {};

  const totalStopTimeSeconds = useMemo(
    () =>
      stops.reduce((sum, stop) => {
        if (!Number.isFinite(Number(stop?.stopTimeMinutes))) return sum;
        return sum + Number(stop.stopTimeMinutes) * 60;
      }, 0),
    [stops],
  );

  const totalStopWindowSeconds = useMemo(
    () =>
      stops.reduce((sum, stop) => {
        if (!Number.isFinite(Number(stop?.timeWindowMinutes))) return sum;
        return sum + Number(stop.timeWindowMinutes) * 60;
      }, 0),
    [stops],
  );

  const totalCheckpointWindowSeconds = useMemo(() => 0, []);

  const finalEtaSeconds = useMemo(() => {
    if (waypointEtas.length) return waypointEtas[waypointEtas.length - 1];
    return totalDurationSeconds ?? null;
  }, [waypointEtas, totalDurationSeconds]);

  const minTourMinutes = useMemo(() => {
    if (finalEtaSeconds == null) return null;
    return Math.round(finalEtaSeconds / 60);
  }, [finalEtaSeconds]);

  const maxTourMinutes = useMemo(() => {
    if (finalEtaSeconds == null) return null;
    const extra = totalStopTimeSeconds + totalStopWindowSeconds + totalCheckpointWindowSeconds;
    return Math.round((finalEtaSeconds + extra) / 60);
  }, [finalEtaSeconds, totalStopTimeSeconds, totalStopWindowSeconds, totalCheckpointWindowSeconds]);

  const totalDistanceMeters = useMemo(() => {
    if (Number.isFinite(routeData?.totalDistanceMeters)) return routeData.totalDistanceMeters;
    if (Array.isArray(routeData?.legs)) {
      return routeData.legs.reduce((sum, leg) => {
        const dist = Number.isFinite(leg?.distanceMeters) ? leg.distanceMeters : 0;
        return sum + dist;
      }, 0);
    }
    return null;
  }, [routeData]);

  const tourMiles = useMemo(() => {
    if (!Number.isFinite(totalDistanceMeters)) return null;
    return totalDistanceMeters * 0.000621371;
  }, [totalDistanceMeters]);

  const tourEstimateLabel =
    minTourMinutes != null && maxTourMinutes != null
      ? minTourMinutes === maxTourMinutes
        ? formatMinutes(minTourMinutes)
        : `${formatMinutes(minTourMinutes)} - ${formatMinutes(maxTourMinutes)}`
      : '—';

  return {
    isIndoor,
    isFreePlacement,
    canBuild,
    canSaveRoute,
    routePoints,
    routeMetrics,
    totalDurationSeconds,
    orderedCheckpoints,
    checkpointSignature,
    legDurationSeconds,
    waypointEtas,
    routeWaypointOrder,
    originOffset,
    stopEtaSeconds,
    timelineMeta,
    stopMetaById,
    checkpointMetaById,
    totalStopTimeSeconds,
    totalStopWindowSeconds,
    totalCheckpointWindowSeconds,
    finalEtaSeconds,
    minTourMinutes,
    maxTourMinutes,
    totalDistanceMeters,
    tourMiles,
    tourEstimateLabel,
  };
}


